/**
 * Empilhamento das séries de armazenamento para o gráfico de composição.
 *
 * A regra da Fase 10 é que **nenhuma categoria é derivada no cliente**: as
 * cinco (mais "Outros") chegam prontas do backend, disjuntas, cada byte numa
 * só. O que este módulo faz não é derivar categoria nenhuma — é empilhar as
 * que chegaram, que é desenho, não medição.
 *
 * Sem imports de propósito: é o que deixa este módulo (e o seu teste) rodar
 * fora do bundler, como `physicalGeometry` e `preparationCopy` já fazem.
 */

/** Um ponto de série temporal, como a API devolve. */
export interface StorageSeriesPoint {
  bucket_start: string;
  value: number;
  is_imputed: boolean;
}

export interface StorageSeriesInput {
  key: string;
  label: string;
  color: string;
  points: StorageSeriesPoint[];
}

export interface StackedSlice {
  bucketStart: string;
  /** Base da faixa neste bucket (topo de tudo que foi empilhado antes). */
  from: number;
  /** Topo da faixa: `from + value`. */
  to: number;
  value: number;
  isImputed: boolean;
}

export interface StackedBand {
  key: string;
  label: string;
  color: string;
  slices: StackedSlice[];
  /** Valor da categoria no bucket mais recente — o que a legenda mostra. */
  latest: number;
}

export interface StackedStorage {
  /** Buckets em ordem cronológica, a união dos que as séries trouxeram. */
  buckets: string[];
  bands: StackedBand[];
  /** Topo da pilha mais alta; 0 quando não há dado. */
  max: number;
  /** Total do bucket mais recente — a soma das categorias, não uma medição. */
  latestTotal: number;
}

/**
 * Empilha as séries na ordem em que vieram (a primeira fica embaixo).
 *
 * Séries com buckets diferentes são alinhadas pela união dos instantes; um
 * bucket que falta numa categoria vale zero nela — não interrompe a pilha,
 * senão o gráfico abriria um degrau que não aconteceu.
 */
export function stackStorageSeries(series: StorageSeriesInput[]): StackedStorage {
  const bucketSet = new Set<string>();
  for (const entry of series) {
    for (const point of entry.points) bucketSet.add(point.bucket_start);
  }
  const buckets = [...bucketSet].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  const tops = new Map<string, number>(buckets.map((bucket) => [bucket, 0]));
  const bands: StackedBand[] = [];

  for (const entry of series) {
    const byBucket = new Map(entry.points.map((point) => [point.bucket_start, point]));
    const slices: StackedSlice[] = buckets.map((bucket) => {
      const point = byBucket.get(bucket);
      const value = point ? point.value : 0;
      const from = tops.get(bucket) ?? 0;
      const to = from + value;
      tops.set(bucket, to);
      return {
        bucketStart: bucket,
        from,
        to,
        value,
        isImputed: point ? point.is_imputed : true,
      };
    });
    bands.push({
      key: entry.key,
      label: entry.label,
      color: entry.color,
      slices,
      latest: slices.length > 0 ? slices[slices.length - 1].value : 0,
    });
  }

  const max = buckets.reduce((acc, bucket) => Math.max(acc, tops.get(bucket) ?? 0), 0);
  const lastBucket = buckets.length > 0 ? buckets[buckets.length - 1] : null;

  return {
    buckets,
    bands,
    max,
    latestTotal: lastBucket === null ? 0 : (tops.get(lastBucket) ?? 0),
  };
}
